<template>
  <div class="kk-designs-search">
    <div class="kk-designs-search__search-input-container">
      <i class="icon-tackle kk-designs-search__search-input--user"></i>

      <input
        ref="input"
        v-model="text"
        type="text"
        :placeholder="$t('search.placeholder')"
        class="kk-designs-search__search-input"
        size="25"
        data-test-id="smart-search"
        @keyup.esc="blur"
        @input="change"
        @keydown="handleKeyDown"
        @click="triggerTackleBox"
      />
    </div>

    <div v-if="isTackleBoxVisible">
      <search-modal
        v-if="showParameterSuggestionBox && selectedItems.length > 0"
        :searched-items="selectedItems"
        :styles="{ top: smartSearch.funcName ? '60px' : '110%' }"
        @select-formula="selectParameter"
        @complete-formula="selectParameter"
      />

      <function-tooltip
        v-if="smartSearch.funcName"
        :function-name="smartSearch.funcName"
        :arg-number="smartSearch.argNum"
        :total-arg="smartSearch.totalArg"
      >
      </function-tooltip>

      <div>
        <search-modal
          v-if="!showParameterSuggestionBox"
          :searched-items="items"
          :has-children="hasChildren"
          @select-formula="selectItem"
          @complete-formula="completeItem"
        />
      </div>
    </div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";
import SearchModal from "./SearchModal.vue";
import FunctionTooltip from "../../process-design/formula/FunctionTooltip.vue";
import { isCell, isRange, removeTextFromEnd } from "../../../utils/util";
import { ProcessTypes, viewIcons } from "../../../utils/icons";
import { HotkeyScopes, keycodeToDirection } from "../../../utils/constants";
import { smartSearchFormulas } from "../../../store/SmartSearch";
import { getCellId, rangeLabelToIndex, sortRange } from "../cell/Utils";
import DesignerMixin from "../DesignerMixin";

export default {
  components: { SearchModal, FunctionTooltip },
  mixins: [DesignerMixin],
  data() {
    return {
      items: [],
      text: "",
      showParameterSuggestionBox: false,
      hasChildren: false,
    };
  },

  computed: {
    isTackleBoxVisible() {
      return this.$store.state.pdesign.isSmartSearchBoxVisible;
    },
    smartSearch() {
      return this.$store.state.smartSearch;
    },
    selectedItems() {
      if (
        this.smartSearch.funcName === "Add Player" ||
        (this.smartSearch.funcName === "Edit Player" && this.smartSearch.argNum === 0)
      )
        return this.smartSearch.processPlayers.filter((item) =>
          item.title.toLowerCase().includes(this.smartSearch.paramFilterText.toLowerCase())
        );
      else if (
        this.smartSearch.funcName === "Add Arrow" &&
        (this.smartSearch.argNum === 0 || this.smartSearch.argNum === 1)
      ) {
        const playersHashTable = this.$store.state.players.playersHashTable;
        const links = this.$store.state.cells.current.design.links;
        const isPostingProcess = this.$store.state.cells.current.type === ProcessTypes.Posting;
        return Object.keys(links)
          .map((key) => {
            if (isPostingProcess)
              return [
                {
                  id: links[key].cellId,
                  title: playersHashTable[key].title + " [Source]",
                  icon: "icon-game_character_C",
                  show: () => true,
                },
                {
                  id: links[key].cellId.replace("-Plus", "-Minus"),
                  title: playersHashTable[key].title + " [Target]",
                  icon: "icon-game_character_C",
                  show: () => true,
                },
              ];
            else
              return {
                id: links[key].cellId,
                title: playersHashTable[key].title,
                icon: "icon-game_character_C",
                show: () => true,
              };
          })
          .flat()
          .filter((item) => item.title.toLowerCase().includes(this.smartSearch.paramFilterText.toLowerCase()));
      } else if (this.smartSearch.funcName === "Add View" && this.smartSearch.argNum === 1) {
        return viewIcons
          .map((item, index) => ({ id: index, title: item.title, icon: item.icon, show: () => true }))
          .filter((item) => item.title.toLowerCase().includes(this.smartSearch.paramFilterText.toLowerCase()));
      }
      return [];
    },
  },
  watch: {
    isTackleBoxVisible(newVal) {
      if (newVal) {
        // Focus the input box when tackle box  becomes true
        this.$refs.input.focus();
        this.getItems();
      } else {
        // Blur the input field and reset the input value when  tackle box becomes false
        this.resetSearchInput();
      }
    },
  },
  methods: {
    triggerTackleBox() {
      this.$store.dispatch("pdesign/setSmartSearchBoxVisibility", true);
    },
    async getItems() {
      this.items = await this.$store.dispatch("smartSearch/search", "");
    },
    resetSearchInput() {
      this.text = ""; // Reset the input value
      this.$refs.input.blur(); // Optionally blur the input field
    },
    include() {
      return [document.querySelector(".kk-designs-search__search-input")];
    },
    blur(e) {
      this.reset();
      this.$refs.input.blur();
    },
    async selectParameter(item) {
      if (!item?.show()) return;
      this.text = removeTextFromEnd(this.text, this.smartSearch.paramFilterText) + item.title;
      this.items = await this.$store.dispatch("smartSearch/search", this.text);
      await this.$store.dispatch("smartSearch/addParameterId", item);
      this.$store.dispatch("smartSearch/recalculateParameters", this.text);
      this.showParameterSuggestionBox = false;
    },
    async change(e) {
      e.preventDefault();
      const text = e.target.value;
      // Check if the text contains the '>' symbol
      if (text.includes(">")) {
        this.hasChildren = true;
      } else {
        // Reset showParameterSuggestionBox if the input is empty
        this.hasChildren = false;
      }
      this.$store.dispatch("smartSearch/recalculateParameters", text);
      this.items = await this.$store.dispatch("smartSearch/search", text);
      this.$store.dispatch("pdesign/setSmartSearchBoxVisibility", true);
      if (this.selectedItems.length > 0) this.showParameterSuggestionBox = true;
      else this.showParameterSuggestionBox = false;
    },
    handleKeyDown(event) {
      if (keycodeToDirection[event.key]) {
        event.preventDefault();
        hotkeys.trigger(keycodeToDirection[event.key], hotkeys.getScope());
      } else if (event.key === "Escape") {
        this.hideSmartSearchBox();
      } else if (event.key === "Enter") {
        hotkeys.trigger(event.key, hotkeys.getScope());
      } else if (event.key === "Tab") {
        event.preventDefault();
        this.hasChildren = true;
        hotkeys.trigger("tab", hotkeys.getScope());
      }
    },

    hideSmartSearchBox() {
      this.$store.dispatch("pdesign/setSmartSearchBoxVisibility", false);
      this.$store.dispatch("smartSearch/resetParameterIds");
    },
    async selectItem(item) {
      if (!item) {
        if (isCell(this.text) || isRange(this.text)) {
          this.goto(this.text);
          this.$store.dispatch("cells/setShouldScroll");
          this.$nextTick(() => {
            this.hideSmartSearchBox();
          });
        }
        return;
      }
      if (!item?.show()) return;
      if (smartSearchFormulas.includes(item.title) && this.smartSearch.parameters?.length <= 0) {
        this.text = item.title + "(";
        this.items = await this.$store.dispatch("smartSearch/search", this.text);
        this.showParameterSuggestionBox = true;
        this.$refs.input.focus();
      } else {
        this.$store.dispatch("smartSearch/callSmartItem", { item, i18n: this.$i18n });
        this.$nextTick(() => {
          this.hideSmartSearchBox();
        });
      }
    },
    async completeItem(item) {
      if (!item?.show()) return;
      if (item.children?.length > 0) {
        this.text = item.title + ">";
        this.items = await this.$store.dispatch("smartSearch/search", this.text);
      } else if (smartSearchFormulas.includes(item.title)) {
        this.text = item.title + "(";
        this.items = await this.$store.dispatch("smartSearch/search", this.text);
        this.showParameterSuggestionBox = true;
      } else {
        this.selectItem(item);
      }
    },
    goto(text) {
      const range = sortRange(rangeLabelToIndex(text));
      if (!this.current || range[2] >= this.columns.length || range[3] >= this.rows.length) return;
      const activeCellId = getCellId(this.rows[range[1]].id, this.columns[range[0]].id);
      this.$store.dispatch("cells/setFormulaRange", { title: text, activeCellId });
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [range[0], range[1]]);
      this.$store.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.ProcessDesigner);
    },
    closeSmartSearch() {
      this.$store.dispatch("pdesign/setSmartSearchBoxVisibility", false);
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables";

::-webkit-scrollbar {
  width: 4px;
  height: 10px;
}

.kk-designs-search {
  position: relative;
  margin-left: 4.4rem;

  &__search-input-container {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0.4rem;
    width: 25rem;
    background: rgba(211, 227, 249, 0.12);
    border-radius: 4px;
    padding-left: 1rem;
    padding-right: 0.5rem;
    font-size: 1.1rem;
  }

  &__search-input {
    flex-grow: 1;
    color: @kk-color-white;
    margin-right: 0.25rem;
    font-size: 14px;

    &--user {
      padding-right: 5px;
    }

    &:focus {
      outline: none;
    }

    /* Chrome, Firefox, Opera, Safari 10.1+ */
    &::placeholder {
      color: @kk-color-white;
      opacity: 0.5; /* Firefox */
    }
    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: @kk-color-white;
    }
    &::-ms-input-placeholder {
      /* Microsoft Edge */
      color: @kk-color-white;
    }
  }
}
</style>
